import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Tag, Button, Row, Col, Statistic, Space, Select, Modal, Input, theme, EmptyState, Popconfirm, message } from '../../ui';
import type { TagTone } from '../../ui';
import { Plus, Check, X, Pencil } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { AdjustmentFormModal } from './AdjustmentFormModal';
import { brand } from '../../assets/brand';
import { fmtQty } from '../../lib/format';
import { formatIst } from '../../lib/time';

const COLOR: Record<string, TagTone> = { DRAFT: 'neutral', SUBMITTED: 'gold', APPROVED: 'green', REJECTED: 'red' };

export function AdjustmentsPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<any>(null); // a SUBMITTED adjustment being edited
  const [status, setStatus] = useState<string | undefined>();
  const [reason, setReason] = useState<string | undefined>();
  const canApprove = usePermission('STOCK.ADJUST.APPROVE'); // approve/reject authority
  const canCreate = usePermission('STOCK.ADJUST.CREATE');

  const { data, isLoading } = useQuery({
    queryKey: ['stock-adjustments', status, reason],
    queryFn: () => request<any[]>({ url: '/stock-adjustments', params: { status, reason } }),
  });
  // Unfiltered set powers the summary cards + reason mix, so they stay accurate
  // (e.g. "Pending approval") regardless of the table's status/reason filter.
  const { data: allData } = useQuery({
    queryKey: ['stock-adjustments-all'],
    queryFn: () => request<any[]>({ url: '/stock-adjustments' }),
  });
  // Resolve the opaque warehouse/product IDs in the table to human labels.
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
  });
  const { data: products } = useQuery({
    queryKey: ['products-list'],
    queryFn: () => request<any>({ url: '/products', params: { pageSize: 200 } }),
  });
  const whMap = useMemo(() => new Map<string, any>((warehouses ?? []).map((w: any) => [String(w.id), w])), [warehouses]);
  const prodMap = useMemo(() => new Map<string, any>(((products?.data) ?? []).map((p: any) => [String(p.id), p])), [products]);

  const approve = useMutation({
    mutationFn: (id: string) => request({ url: `/stock-adjustments/${id}/approve`, method: 'POST' }),
    onSuccess: () => { message.success('Approved'); qc.invalidateQueries({ queryKey: ['stock-adjustments'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Approval failed'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      request({ url: `/stock-adjustments/${id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Rejected'); qc.invalidateQueries({ queryKey: ['stock-adjustments'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const askReject = (id: string) => {
    let txt = '';
    Modal.confirm({
      title: 'Reject adjustment?',
      content: <Input.TextArea rows={3} placeholder="Why is this being rejected?" onChange={(e) => (txt = e.target.value)} />,
      okType: 'danger',
      onOk: () => reject.mutate({ id, reason: txt || 'Not approved' }),
    });
  };

  const stats = useMemo(() => {
    const rows = allData ?? [];
    const submitted = rows.filter((r) => r.status === 'SUBMITTED').length;
    const approved = rows.filter((r) => r.status === 'APPROVED').length;
    // Quantity totals reflect ledger impact → count only posted (APPROVED) rows.
    const posted = rows.filter((r) => r.status === 'APPROVED');
    const inQty = posted.filter((r) => r.direction === 'IN').reduce((s, r) => s + Number(r.qty ?? 0), 0);
    const outQty = posted.filter((r) => r.direction === 'OUT').reduce((s, r) => s + Number(r.qty ?? 0), 0);
    return { total: rows.length, submitted, approved, inQty, outQty };
  }, [allData]);

  const reasonEntries = useMemo(() => {
    const byReason: Record<string, number> = {};
    for (const r of allData ?? []) byReason[r.reason ?? 'OTHER'] = (byReason[r.reason ?? 'OTHER'] ?? 0) + 1;
    return Object.entries(byReason);
  }, [allData]);

  const reasonChart = useMemo(() => {
    const total = reasonEntries.reduce((s, [, v]) => s + v, 0);
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: {
        bottom: 0, left: 'center', icon: 'circle', itemWidth: 10, itemHeight: 10, itemGap: 14,
        textStyle: { color: token.colorTextSecondary, fontSize: 12 },
      },
      // Total lives in the donut hole — no outer slice labels, so nothing overlaps.
      title: {
        text: String(total), subtext: 'Total', left: 'center', top: '36%', textAlign: 'center', itemGap: 2,
        textStyle: { color: token.colorText, fontSize: 24, fontWeight: 700 },
        subtextStyle: { color: token.colorTextTertiary, fontSize: 11 },
      },
      series: [{
        type: 'pie',
        radius: ['54%', '76%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 6, borderColor: token.colorBgContainer, borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        emphasis: { scaleSize: 6, itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' } },
        data: reasonEntries.map(([k, v]) => ({ name: k, value: v })),
      }],
      color: [brand.primary, brand.harvest, '#5b9bd5', '#ed7d31', '#a5a5a5', '#70ad47', '#264478'],
    };
  }, [reasonEntries, token]);

  const columns: DataTableColumn<any>[] = [
    { key: 'adjustmentNo', title: 'Adj No', dataIndex: 'adjustmentNo', sortable: true, alwaysVisible: true },
    {
      key: 'warehouseId', title: 'Warehouse', dataIndex: 'warehouseId', sortable: true,
      render: (v: string) => { const w = whMap.get(String(v)); return w ? `${w.code} · ${w.name}` : v; },
    },
    {
      key: 'productId', title: 'Product', dataIndex: 'productId', sortable: true,
      render: (v: string) => { const p = prodMap.get(String(v)); return p ? `${p.sku} · ${p.name}` : v; },
    },
    { key: 'batchId', title: 'Batch', dataIndex: 'batchId', defaultVisible: false },
    { key: 'qty', title: 'Qty', dataIndex: 'qty', sortable: true, align: 'right' },
    {
      key: 'direction', title: 'Direction', dataIndex: 'direction', sortable: true,
      render: (v: string) => <Tag tone={v === 'IN' ? 'green' : 'red'}>{v}</Tag>,
    },
    { key: 'reason', title: 'Reason', dataIndex: 'reason' },
    { key: 'evidenceUrl', title: 'Evidence', dataIndex: 'evidenceUrl', defaultVisible: false, render: (v: string) => v ? <a href={v} target="_blank" rel="noopener">view</a> : '—' },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (s: string) => <Tag tone={COLOR[s]}>{s}</Tag>,
    },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false, render: (v: string) => formatIst(v) },
    ...(canApprove || canCreate ? [{
      key: 'action', title: 'Action', alwaysVisible: true, width: 240,
      render: (_: any, r: any) =>
        r.status === 'SUBMITTED' ? (
          <Space>
            {canCreate && (
              <Button size="sm" variant="text" icon={<Pencil size={16} />} onClick={() => setEditing(r)}>
                Edit
              </Button>
            )}
            {canApprove && (
              <>
                <Popconfirm
                  title="Approve adjustment?"
                  description="This posts the stock movement to the ledger and cannot be undone."
                  okText="Approve"
                  onConfirm={() => approve.mutate(r.id)}
                >
                  <Button size="sm" variant="primary" icon={<Check size={16} />} loading={approve.isPending && approve.variables === r.id}>
                    Approve
                  </Button>
                </Popconfirm>
                <Button size="sm" variant="danger" icon={<X size={16} />} onClick={() => askReject(r.id)}>
                  Reject
                </Button>
              </>
            )}
          </Space>
        ) : null,
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Stock Adjustments</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate && !embedded ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setShowNew(true)}>
              New Adjustment
            </Button>
          ) : undefined}
        </div>
      </div>

      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col xs={12} md={4}><Card><Statistic title="Total" value={stats.total} /></Card></Col>
        <Col xs={12} md={5}><Card><Statistic title="Pending approval" value={stats.submitted} valueStyle={{ color: '#E7B22C' }} /></Card></Col>
        <Col xs={12} md={5}><Card><Statistic title="Approved" value={stats.approved} valueStyle={{ color: brand.primary }} /></Card></Col>
        <Col xs={12} md={5}><Card><Statistic title="Total IN qty" value={stats.inQty} formatter={(v) => fmtQty(v)} valueStyle={{ color: brand.primary }} /></Card></Col>
        <Col xs={12} md={5}><Card><Statistic title="Total OUT qty" value={stats.outQty} formatter={(v) => fmtQty(v)} valueStyle={{ color: '#cf1322' }} /></Card></Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card title="Reason mix" className="mb-3">
            {reasonEntries.length === 0
              ? <EmptyState title="No adjustments yet" />
              : <ReactECharts option={reasonChart} style={{ height: 210 }} notMerge />}
          </Card>
        </Col>
        <Col xs={24} md={16}>
          <Card>
            <DataTable
              storageKey="adjustments"
              rowKey="id"
              loading={isLoading}
              dataSource={data ?? []}
              columns={columns}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              toolbarLeft={
                <Space>
                  <Select
                    placeholder="Status"
                    allowClear
                    style={{ width: 140 }}
                    value={status}
                    onChange={setStatus}
                    options={Object.keys(COLOR).map((s) => ({ value: s, label: s }))}
                  />
                  <Select
                    placeholder="Reason"
                    allowClear
                    style={{ width: 200 }}
                    value={reason}
                    onChange={setReason}
                    options={['PHYSICAL_COUNT', 'DAMAGE', 'EXPIRY', 'THEFT', 'SAMPLE', 'PRODUCTION_LOSS', 'OTHER']
                      .map((s) => ({ value: s, label: s }))}
                  />
                </Space>
              }
            />
          </Card>
        </Col>
      </Row>

      <AdjustmentFormModal open={showNew || !!editing} editing={editing} onClose={() => { setShowNew(false); setEditing(null); }} />
    </div>
  );
}
